/**
 * ⭐ board 2871 - THE RETURN LEG HELEN TYPES: a Dublin day and an `HH:MM`, read
 * off a request body and checked against the outbound. ONE rule for every door
 * that writes `bookings.return_at` from the kiosk (the quote, park and reschedule
 * routes) and for the screens that check it before posting, so the client and
 * the server cannot disagree about which return is acceptable.
 *
 * ⛔ WALL CLOCK, NEVER INSTANTS. The comparison is (day, `HH:MM`) as strings, the
 * way `fallsAfterReturn` (core/reschedule-booking.ts) and the phone-in route
 * already compare. `dublinInstant` resolves a repeated October hour to the
 * EARLIER instant and moves a skipped March time forward, so two instants can
 * order differently from the clock Helen read off the wall.
 *
 * Shared because the browser bundle and the server both import it; it holds no
 * copy, only codes.
 */
import { dublinInstant } from './dublin-date.js';
import { RESCHEDULE_TIME_RE } from './reschedule-reasons.js';

/** A return Helen stated: the Dublin day, the `HH:MM`, and the instant they name. */
export type ReturnLeg = { day: string; time: string; at: string };

/** What a body said about the return. */
export type ReturnLegRead =
  /** No `returnTime` key: do not touch the stored return. */
  | { kind: 'absent' }
  /** `returnTime` is '': clear the stored return. */
  | { kind: 'clear' }
  | { kind: 'set'; leg: ReturnLeg }
  /** Anything else, including a `returnDay` with no `returnTime`. */
  | { kind: 'invalid' };

/** Why a return is refused. `invalid` is a shape the readers will not take. */
export type ReturnLegCause = 'invalid' | 'before' | 'too_far';

/** The coded 400 each cause answers with. ONE table: the routes write it and the
 *  client reads it back (`returnLegCauseOf`), never the server's sentence (SEC-5). */
export const RETURN_LEG_CODES: Readonly<Record<ReturnLegCause, string>> = {
  invalid: 'return_invalid',
  before: 'return_before_journey',
  too_far: 'return_too_far',
};

/** The cause a coded 400 names, or null for any other code. */
export function returnLegCauseOf(code: unknown): ReturnLegCause | null {
  const hit = (Object.keys(RETURN_LEG_CODES) as ReturnLegCause[]).find((c) => RETURN_LEG_CODES[c] === code);
  return hit ?? null;
}

/**
 * Read `returnDay` / `returnTime` off a body, as OWN properties only, so a
 * polluted prototype cannot supply either.
 */
export function readReturnLeg(body: Record<string, unknown>): ReturnLegRead {
  if (!Object.hasOwn(body, 'returnTime')) {
    return Object.hasOwn(body, 'returnDay') ? { kind: 'invalid' } : { kind: 'absent' };
  }
  const time = body['returnTime'];
  if (time === '') return { kind: 'clear' };
  const day = Object.hasOwn(body, 'returnDay') ? body['returnDay'] : undefined;
  if (typeof time !== 'string' || !RESCHEDULE_TIME_RE.test(time)) return { kind: 'invalid' };
  if (typeof day !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return { kind: 'invalid' };
  const at = dublinInstant(day, time);
  return at === null ? { kind: 'invalid' } : { kind: 'set', leg: { day, time, at } };
}

/**
 * ⭐ board 2871 (fix round 2) - is this a real calendar day, `YYYY-MM-DD`? The one
 * test for a return-day DEFAULT (the site form's stated day), so a shape-valid but
 * impossible day (`2026-02-31`, from rows written before the form checked dates)
 * is never shown, and never sent, as a return date.
 */
export function isReturnDay(s: string | null | undefined): s is string {
  return typeof s === 'string' && dublinInstant(s, '12:00') !== null;
}

/** The same month and day one year on, as a key that compares as a string. */
function oneYearOn(day: string): string {
  return `${String(Number(day.slice(0, 4)) + 1)}${day.slice(4)}`;
}

/**
 * null when the return may stand against this outbound, else why not:
 *   · `before`  - the return is on an earlier day, or on the same day at or
 *                 before the outbound's `HH:MM` (equal is refused);
 *   · `too_far` - the return is a year or more after the outbound's day (a
 *                 mistyped year).
 * A null outbound day refuses nothing (an undated walk-in has nothing to compare);
 * an outbound time that is not `HH:MM` (public-form free text) compares days only.
 */
export function returnLegRefusal(
  ret: { day: string; time: string },
  out: { day: string | null; time: string | null },
): 'before' | 'too_far' | null {
  if (out.day === null) return null;
  if (ret.day < out.day) return 'before';
  const outTime = (out.time ?? '').trim();
  if (ret.day === out.day && RESCHEDULE_TIME_RE.test(outTime) && ret.time <= outTime) return 'before';
  // ⭐ Captain review (security Low 2): EXCLUSIVE. A same-day return typed one year
  // forward lands exactly on `oneYearOn`, and the readers print no year by default.
  if (ret.day >= oneYearOn(out.day)) return 'too_far';
  return null;
}
